import api from '@/lib/axios';
import { fetchSSEStream } from '@/lib/fetchStream';
import { getStoredAuthToken } from '@/lib/authStorage';
import { ensureAiTaskCredits, type AiTaskCreditsGuardOptions } from '@/lib/aiTaskCredits';
import { notifyPointBalanceChanged } from '@/lib/creditBalance';
import {
  AIGenerateRequest,
  AIGenerateResponse,
  AiMediaGenerateResponse,
  AiMediaAvailableTask,
  AiMediaAvailableTaskDetailRequest,
  AiMediaImageGenerateRequest,
  AiMediaTaskDetailRequest,
  AutopilotInstructionRecord,
  AutopilotInstructionRecordCreateRequest,
  AutopilotInstructionRecordListRequest,
  AutopilotInstructionRecordListResponse,
  UserAiGenerateRecordListRequest,
  UserAiGenerateRecordListResponse,
  AgentSessionListRequest,
  AgentSessionListResponse,
  AgentMessageListRequest,
  AgentMessageListResponse,
  AccountDetail,
  AccountDetailListRequest,
  AccountDetailListResponse,
  UserPointAccount,
  PointTransactionListRequest,
  PointTransactionListResponse,
  PromptTemplate,
  PromptTemplateRequest,
  UrlFetchHtmlRequest,
  UrlFetchHtmlResponse,
} from '@/types/user';

type AIGenerateOptions = AiTaskCreditsGuardOptions & {
  suppressErrorToast?: boolean;
};

type AIGenerateStreamOptions = AIGenerateOptions & {
  preferFinalContent?: boolean;
};

/**
 * 用户模块 API
 * 封装 AI 内容生成及 Agent 会话管理功能
 */
export const userApi = {
  /**
   * AI内容生成 (SSE 流式)
   * @param data 任务编码及输入消息
   * @param onMessage 收到消息块时的回调 (用于实时展示生成过程)
   */
  aiGenerateStream: async (
    data: AIGenerateRequest,
    onMessage?: (chunk: string) => void,
    options?: AIGenerateStreamOptions,
  ): Promise<AIGenerateResponse> => {
    const endpoint = `/lowcode-business/api/user/ai/generate/stream`;
    const token = getStoredAuthToken();

    await ensureAiTaskCredits(data.taskCode, options);

    try {
      const { content, finalData, truncated } = await fetchSSEStream<AIGenerateResponse>(
        endpoint,
        data,
        {
          onMessage,
          suppressErrorToast: options?.suppressErrorToast,
          showCreditsModal: options?.showCreditsModal,
          creditsModalTitle: options?.creditsModalTitle,
          creditsModalCopy: options?.creditsModalCopy,
          creditsModalProductArea: options?.creditsModalProductArea,
          creditsModalSurface: options?.creditsModalSurface,
          creditsModalStage: options?.creditsModalStage,
          creditsModalTrigger: options?.creditsModalTrigger,
          creditsModalUiType: options?.creditsModalUiType,
          creditsModalSource: options?.creditsModalSource,
          creditsModalOperation: options?.creditsModalOperation,
          creditsModalTaskCode: data.taskCode,
          creditsModalMetadata: options?.creditsModalMetadata,
        },
        { token: token || '' },
      );

      const finalContent = (() => {
        if (!finalData || typeof finalData !== 'object') return '';
        const record = finalData as Record<string, unknown>;
        if (typeof record.content === 'string') return record.content;
        if (typeof record.resultContent === 'string') return record.resultContent;
        const nestedData = record.data;
        if (nestedData && typeof nestedData === 'object') {
          const nestedRecord = nestedData as Record<string, unknown>;
          if (typeof nestedRecord.content === 'string') return nestedRecord.content;
          if (typeof nestedRecord.resultContent === 'string') return nestedRecord.resultContent;
        }
        return '';
      })();
      const responseContent =
        options?.preferFinalContent && finalContent ? finalContent : content || finalContent;

      return {
        ...(finalData || {}),
        content: responseContent,
        rawStreamContent: content,
        truncated,
      } as AIGenerateResponse;
    } finally {
      notifyPointBalanceChanged();
    }
  },

  aiGenerate: async (
    data: AIGenerateRequest,
    options?: AIGenerateOptions,
  ): Promise<AIGenerateResponse> => {
    await ensureAiTaskCredits(data.taskCode, options);
    try {
      return await api.post('/lowcode-business/api/user/ai/generate', data, {
        _suppressErrorToast: options?.suppressErrorToast,
      });
    } finally {
      notifyPointBalanceChanged();
    }
  },

  generateAiMediaImageSync: async (
    data: AiMediaImageGenerateRequest,
    options?: AIGenerateOptions,
  ): Promise<AiMediaGenerateResponse> => {
    await ensureAiTaskCredits(data.taskCode, options);
    try {
      return await api.post('/lowcode-business/api/user/ai/media/image/generate/sync', data, {
        _suppressErrorToast: options?.suppressErrorToast,
      });
    } finally {
      notifyPointBalanceChanged();
    }
  },

  generateAiMediaImageAsync: async (
    data: AiMediaImageGenerateRequest,
    options?: AIGenerateOptions,
  ): Promise<AiMediaGenerateResponse> => {
    await ensureAiTaskCredits(data.taskCode, options);
    try {
      return await api.post('/lowcode-business/api/user/ai/media/image/generate/async', data, {
        _suppressErrorToast: options?.suppressErrorToast,
      });
    } finally {
      notifyPointBalanceChanged();
    }
  },

  getAiMediaTaskDetail: (
    data: AiMediaTaskDetailRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<AiMediaGenerateResponse> =>
    api.post('/lowcode-business/api/user/ai/media/task/detail', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),

  getAiMediaAvailableTaskDetail: (
    data: AiMediaAvailableTaskDetailRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<AiMediaAvailableTask> =>
    api.post('/lowcode-business/api/user/ai/media/task/available/detail', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),

  getAiGenerateRecords: (
    data: UserAiGenerateRecordListRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<UserAiGenerateRecordListResponse> =>
    api.post('/lowcode-business/api/user/ai/record/findListByPage', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),

  getAutopilotInstructionRecords: (
    data: AutopilotInstructionRecordListRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<AutopilotInstructionRecordListResponse> =>
    api.post('/lowcode-business/api/user/site/autopilot/instruction-record/findListByPage', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),

  createAutopilotInstructionRecord: (
    data: AutopilotInstructionRecordCreateRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<AutopilotInstructionRecord> =>
    api.post('/lowcode-business/api/user/site/autopilot/instruction-record/create', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),

  /**
   * 获取聊天对话列表
   * @param data 包含项目ID及分页信息
   */
  getSessions: (data: AgentSessionListRequest): Promise<AgentSessionListResponse> => {
    return api.post('/lowcode-business/api/user/agent-session/sessions', data, {
      _suppressErrorToast: true,
    });
  },

  /**
   * 获取指定对话的消息列表
   * @param data 包含项目ID、会话ID及分页信息
   */
  getMessages: (data: AgentMessageListRequest): Promise<AgentMessageListResponse> => {
    return api.post('/lowcode-business/api/user/agent-session/messages', data, {
      _suppressErrorToast: true,
    });
  },

  /**
   * 获取账户积分明细（分页）
   */
  getAccountDetailList: (params: AccountDetailListRequest): Promise<AccountDetailListResponse> => {
    return api.post('/lowcode-business/api/user/account/detail/findListByPage', params);
  },

  /**
   * 获取单条账户明细（by detailNo）
   */
  getAccountDetail: (params?: { detailNo?: string }): Promise<AccountDetail> => {
    return api.post('/lowcode-business/api/user/account/detail/find', params ?? {});
  },

  /**
   * 获取用户积分变动明细（分页）
   */
  getPointTransactions: (
    params: PointTransactionListRequest,
  ): Promise<PointTransactionListResponse> => {
    return api.post('/lowcode-business/api/user/point/transaction/findListByPage', params);
  },

  /**
   * 获取用户积分账户信息（余额）
   */
  getPointAccount: (): Promise<UserPointAccount> => {
    return api.post('/lowcode-business/api/user/point/balance');
  },

  /**
   * 按类型查询提示词列表
   */
  getPromptTemplates: (data: PromptTemplateRequest): Promise<PromptTemplate[]> => {
    return api.post('/lowcode-business/api/user/prompt-template/findByType', data);
  },

  fetchUrlHtml: (
    data: UrlFetchHtmlRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<UrlFetchHtmlResponse> =>
    api.post('/lowcode-business/api/user/url/fetch-html', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    }),
};
